import type { AppRole, Profile } from '../lib/types'

/**
 * Roles ride on the auth token; the backend exposes them on the profile via
 * `GET /users/me` (`Profile.roles` — `{ ...profile, roles: user.roles }`, live since
 * 2026-07). If `me.roles` is ever `undefined` (pre-load / auth off), the helpers
 * below resolve to `false` (no role ⇒ treated as a plain employee).
 *
 * Roles the UI shows the feed-post composer to (news.tsx → POST /feed/posts).
 * ⚠️ UI-gate ONLY — does NOT mirror a backend gate: `POST /feed/posts` is not role-gated
 * (any authenticated user may post). The separate admin `/news` module is `@Roles('admin')`
 * (no UI yet). So this list only decides who *sees* the composer, not who the API accepts.
 */
export const NEWS_AUTHOR_ROLES: AppRole[] = ['admin', 'hr']

/** True if the user carries at least one of `roles`. */
export function hasAnyRole(me: Pick<Profile, 'roles'> | null | undefined, roles: AppRole[]): boolean {
  const mine = me?.roles ?? []
  return roles.some((r) => mine.includes(r))
}

/**
 * Does the UI show the feed-post composer to this user? UI-gate only — the backend
 * `POST /feed/posts` does not enforce it (see NEWS_AUTHOR_ROLES). For a strictly
 * HR-only gate, drop `'admin'` from `NEWS_AUTHOR_ROLES`.
 */
export function canCreateNews(me: Pick<Profile, 'roles'> | null | undefined): boolean {
  return hasAnyRole(me, NEWS_AUTHOR_ROLES)
}

/** Roles allowed to edit other people's HR cards — mirrors the backend gate on `PATCH /users/:id`. */
export const PEOPLE_EDITOR_ROLES: AppRole[] = ['admin', 'hr']

/**
 * Can the signed-in user edit other employees' HR cards (`PATCH /users/:id`,
 * `GET /users/:id/full`)? UI-gate only — the backend re-checks and returns 403.
 */
export function canEditPeople(me: Pick<Profile, 'roles'> | null | undefined): boolean {
  return hasAnyRole(me, PEOPLE_EDITOR_ROLES)
}

/**
 * Roles that may moderate the feed — edit/delete ANY post, not only their own.
 * Mirrors the backend `assertCanModerate` (author OR admin/hr) on
 * PATCH/DELETE /feed/posts/:id. Comments stay author/admin (assertCanModify).
 */
const POST_MODERATOR_ROLES: AppRole[] = ['admin', 'hr']

/**
 * Can the signed-in user edit/delete this post? Its author, or a moderator
 * (admin/hr). UI-gate only — the backend re-checks and returns 403.
 */
export function canModeratePost(
  me: Pick<Profile, 'id' | 'roles'> | null | undefined,
  authorId: string | null | undefined,
): boolean {
  if (authorId && me?.id === authorId) return true
  return hasAnyRole(me, POST_MODERATOR_ROLES)
}

/**
 * Can the signed-in user create/edit/delete editorial news? admin/hr — mirrors the
 * backend `@Roles('admin','hr')` on POST/PATCH/DELETE /news. UI-gate; backend re-checks.
 */
export function canManageNews(me: Pick<Profile, 'roles'> | null | undefined): boolean {
  return hasAnyRole(me, ['admin', 'hr'])
}
